import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Auth } from '@freelancer/auth';
import type { CCFormExtraPayload } from '@freelancer/billing';
import type { DatastoreCollection } from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  CreditCard,
  DepositPageVarsCollection,
  DepositPageVarsWorldpayDdc,
} from '@freelancer/datastore/collections';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type {
  ResponseData,
  SuccessResponseData,
} from '@freelancer/freelancer-http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import type { InAppBrowserRef } from '@freelancer/in-app-browser';
import { InAppBrowser } from '@freelancer/in-app-browser';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import {
  OverlayTypes,
  PaymentsErrorType,
  PaymentsEventType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsPopUpService } from '@freelancer/payments-popup';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { assertNever, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { BackendErrorTypes } from 'api-typings/error-distribution';
import type {
  DepositConfirmResponseApi,
  DepositRequestActionApi,
  DepositRequestApi,
  DepositRequestConfirmationApi,
  ReferenceDepositRequestApi,
  ThreedsBrowserInfoApi,
  ThreedsDataApi,
  ThreedsResponseDataApi,
} from 'api-typings/payments/payments';
import {
  DepositMethodApi,
  ThreedsCapabilityApi,
} from 'api-typings/payments/payments';
import type { Observable } from 'rxjs';
import { firstValueFrom, fromEvent, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import type {
  PreprocessMilestonesRequest,
  PreprocessMilestonesResponse,
  ThreeDsBaseContext,
  ThreeDSContext,
} from './deposits.model';
import { ThreeDSState } from './deposits.types';
/**
 * Form data item
 */
export interface FormDataItem {
  name: string;
  value: string;
}

/**
 * 3DS messages types posted from popup window
 */
enum ThreeDSMessageType {
  TRIGGER = 'breakTo3ds',
  RETURN = 'returnFrom3DS',
}

@UntilDestroy({ className: 'Deposits' })
@Injectable({
  providedIn: 'root',
})
export class Deposits {
  @RepetitiveSubscription()
  private inAppBrowserMessageSubscription?: Subscription;
  private browser: InAppBrowserRef;
  private threeDSPopUp: Window | undefined;
  private paymentPopUp: Window | undefined;

  /**
   * a unique id of the deposit so that we can ensure that if
   * someone calls creditCardDeposit multiple times
   * we only finish processing the most recent call
   */
  private depositReference?: number;
  constructor(
    private auth: Auth,
    private freelancerHttp: FreelancerHttp,
    private http: HttpClient,
    private paymentsMessaging: PaymentsMessagingService,
    private paymentsUtils: PaymentsUtils,
    private location: Location,
    private inAppBrowser: InAppBrowser,
    private pwa: Pwa,
    private popUpService: PaymentsPopUpService,
    private tracking: Tracking,
    private activatedRoute: ActivatedRoute,
    private datastore: Datastore,
    private localStorage: LocalStorage,
  ) {}

  /**
   * Deposit request setup through API
   *
   * @param payload Post request payload for deposit API
   */
  deposit(
    payload: DepositRequestApi,
  ): Observable<ResponseData<DepositRequestActionApi, any>> {
    return this.auth.getUserId().pipe(
      switchMap(userId =>
        this.freelancerHttp.post<DepositRequestActionApi>(
          'payments/0.1/deposits',
          { ...payload, user_id: toNumber(userId) },
        ),
      ),
      take(1),
    );
  }

  /**
   * Deposit request confirm through API
   *
   * @param payload Post request payload for deposit API
   */
  depositUpdate(
    payload: DepositRequestConfirmationApi,
  ): Promise<
    ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >
  > {
    return firstValueFrom(
      this.auth.getUserId().pipe(
        switchMap(userId =>
          // Response is {status: 'success', requestId: ...}
          this.freelancerHttp.put<
            DepositConfirmResponseApi,
            BackendErrorTypes['deposit_confirm']
          >('payments/0.1/deposits', {
            ...payload,
            user_id: toNumber(userId),
          }),
        ),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Recurring payment through API
   *
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway recurring payment gateway
   */
  authorization(
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    cartId?: number,
    creditCardGateway?: string,
    flnBillingData?: string,
    threedsCapability?: ThreedsCapabilityApi,
    threedsResponseData?: ThreedsResponseDataApi,
    invoiceId?: number,
    threedsBrowserInfo?: ThreedsBrowserInfoApi,
  ): Promise<
    ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >
  > {
    return firstValueFrom(
      this.auth.getUserId().pipe(
        switchMap(userId => {
          const payload = this.getAuthorizationPayload(
            toNumber(userId),
            depositMethod,
            billingAgreementId,
            amount,
            depositCurrencyId,
            paymentCurrencyId,
            threatMetrixSessionId,
            trackingToken,
            creditCardGateway,
            cartId,
            flnBillingData,
            threedsCapability,
            threedsResponseData,
            invoiceId,
            threedsBrowserInfo,
          );
          // Response is {status: 'success', requestId: ...}
          return this.freelancerHttp.put<
            DepositConfirmResponseApi,
            BackendErrorTypes['deposit_confirm']
          >('payments/0.1/authorization', payload);
        }),
        untilDestroyed(this),
      ),
    );
  }

  /**
   * Populates the browser info fields in CCFormExtraPayload.
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   *
   * @param extraPayLoad CCFormExtraPayload object to populate.
   */
  populateBrowserInfo(extraPayLoad: CCFormExtraPayload): CCFormExtraPayload {
    const screen = window && window.screen;
    const navigator = window && window.navigator;
    const language = navigator && navigator.language;

    return {
      ...extraPayLoad,
      browserScreenWidth: screen ? screen.width.toString() : '',
      browserScreenHeight: screen ? screen.height.toString() : '',
      browserColourDepth: screen ? screen.colorDepth.toString() : '',
      browserTZoneOffset: new Date().getTimezoneOffset().toString(),
      browserLanguage: language ? language.toString() : '',
      // eslint-disable-next-line deprecation/deprecation
      browserJavaEnabled: navigator?.javaEnabled()?.toString() ?? 'false',
      browserJScriptEnabled: 'true',
    };
  }

  /**
   * Generate browser info fields for 3DSv2
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   */
  generateThreedsBrowserInfo(): ThreedsBrowserInfoApi {
    const screen = window && window.screen;
    const navigator = window && window.navigator;
    const language = navigator && navigator.language;

    return {
      screen_width: screen ? screen.width.toString() : '',
      screen_height: screen ? screen.height.toString() : '',
      colour_depth: screen ? screen.colorDepth.toString() : '',
      tzone_offset: new Date().getTimezoneOffset().toString(),
      language: language ? language.toString() : '',
      // eslint-disable-next-line deprecation/deprecation
      java_enabled: navigator?.javaEnabled()?.toString() ?? 'false',
      jscript_enabled: 'true',
    };
  }

  /**
   * Creates a ThreeDSContext object based
   * on the  received ThreedsDataApi.
   *
   * @param threeDSApiData 3DS data field in the response payload to the
   *        DepositConfirm API request.
   * @param currentCtx threeDSContext
   */
  createThreeDSContext(
    threeDSApiData: ThreedsDataApi,
    currentCtx?: ThreeDSContext,
  ): ThreeDSContext | undefined {
    const curState = currentCtx ? currentCtx.type : undefined;

    if (
      !threeDSApiData.threeds_state ||
      !threeDSApiData.invoice_id ||
      !threeDSApiData.credit_card_gateway
    ) {
      return;
    }

    const baseCtx: ThreeDsBaseContext = {
      ccGateway: threeDSApiData.credit_card_gateway,
      invoiceId: threeDSApiData.invoice_id,
      fxTransId: threeDSApiData.fx_transaction_id,
      requestAmount: threeDSApiData.request_amount,
    };

    switch (threeDSApiData.threeds_state) {
      case ThreeDSState.REDIRECT: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          const redirectData = threeDSApiData.redirect_flow_data;
          if (!redirectData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.REDIRECT,
            issuerUrl: redirectData.issuer_url,
            issuerReturnUrl: redirectData.issuer_return_url,
            paRequest: redirectData.payer_authentication_request,
            merchantData: redirectData.merchant_data,
            echoData: redirectData.echo_data,
            cookieData: redirectData.cookie_data,
          };
        }
        break;
      }

      case ThreeDSState.IDENTIFY: {
        if (!curState) {
          const identifyData = threeDSApiData.identify_flow_data;
          if (!identifyData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.IDENTIFY,
            serverTransID: identifyData.server_transaction_id,
            issuerUrl: identifyData.issuer_url,
            issuerReturnUrl: identifyData.issuer_return_url,
            threeDS2token: identifyData.threeds2_token,
          };
        }
        break;
      }

      case ThreeDSState.CHALLENGE: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          const challengeData = threeDSApiData.challenge_flow_data;
          if (!challengeData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.CHALLENGE,
            serverTransID: challengeData.server_transaction_id,
            acsTransID: challengeData.acs_transaction_id,
            messageVersion: challengeData.message_version,
            messageType: challengeData.message_type,
            issuerUrl: challengeData.issuer_url,
            threeDS2token: challengeData.threeds2_token,
          };
        }
        break;
      }
      default:
    }
  }

  /**
   * Initial 3DS flow
   *
   * @param context threeDSContext
   * @param useBackend boolean flag to check if backend will handle the gateway logic for 3DS request
   */
  initiate3DSFlow({
    context,
    useBackend = false,
  }: {
    context: ThreeDSContext;
    useBackend?: boolean;
  }): void {
    switch (context.type) {
      case ThreeDSState.REDIRECT: {
        if (useBackend) {
          this.redirectUserTo3dsWaypoint(context.invoiceId);
          break;
        }

        if (context.issuerReturnUrl === 'gchosted') {
          //
          // GC MerchantLink Fast (Hosted) implementation. We are piggy backing on 3DS V1 'Redirect' to
          // push this feature out quickly. In GC Hosted flow, return URL has be sent to the gateway
          // during the initial payment request, and therefore 'context.issuerReturnUrl' is not required.
          // We are using the special value 'gchosted' switch between the original 'REDIRECT' and
          // 'GC HOSTED' flows.
          //
          // FIXME: T267853 - Create a new 'context.type' (e.g. 'hosted') instead. This will require
          //

          if (!this.isNative() && this.threeDSPopUp) {
            this.threeDSPopUp.location.href = context.issuerUrl;
          }
        } else {
          const formItems: FormDataItem[] = [
            { name: 'PaReq', value: context.paRequest },
            { name: 'TermUrl', value: context.issuerReturnUrl },
            { name: 'IsModal', value: 'true' },
          ];
          if (context.merchantData) {
            formItems.push({ name: 'MD', value: context.merchantData });
          }
          this.setupAndPostForm(context.issuerUrl, formItems);
        }

        break;
      }

      case ThreeDSState.IDENTIFY: {
        if (useBackend) {
          this.redirectUserTo3dsWaypoint(context.invoiceId);
          break;
        }

        const dataObj = {
          threeDSServerTransID: context.serverTransID,
          threeDSMethodNotificationURL: context.issuerReturnUrl,
        };

        const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));

        const formItems: FormDataItem[] = [
          { name: 'threeDSMethodData', value: encodedJSON },
        ];

        this.setupAndPostForm(context.issuerUrl, formItems);

        break;
      }

      case ThreeDSState.CHALLENGE: {
        if (useBackend) {
          /**
           * We will still store messageType in local storage if gateway is WorldPay
           * because it will be used in the deposit/authorization PUT request.
           * See generateConfirmationRequest/authorization.
           */
          if (context.serverTransID === '' && context.messageVersion === '') {
            this.localStorage.set(
              'threeDSChallengeCookie',
              context.messageType,
            );
          }
          this.redirectUserTo3dsWaypoint(context.invoiceId);
          break;
        }

        let formItems: FormDataItem[];
        if (context.serverTransID === '' && context.messageVersion === '') {
          // We are in WorldPay flow when serverTransID and messageVersion
          // from the GAF response are empty because other flows (Adyen/GC) will
          // have values for these variables
          this.localStorage.set('threeDSChallengeCookie', context.messageType);
          formItems = [{ name: 'JWT', value: context.acsTransID }];
        } else {
          // The `challengeWindowSize` describes the popup window (dimensions?) to
          // the issuer. Can be any of the following flags:
          //   01 - 250px x 400px
          //   02 - 390px x 400px
          //   03 - 500px x 600px
          //   04 - 600px x 400px
          //   05 - 100%  x 100%
          const dataObj = {
            threeDSServerTransID: context.serverTransID,
            acsTransID: context.acsTransID,
            messageVersion: context.messageVersion,
            challengeWindowSize: '05',
            messageType: context.messageType,
          };
          const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));
          formItems = [{ name: 'creq', value: encodedJSON }];
        }
        this.setupAndPostForm(context.issuerUrl, formItems);
        break;
      }
      default:
        return assertNever(context);
    }
  }

  /**
   * Create 3DS context for different 3DS state
   *
   * @param context 3DS context data object
   * @param eventData PostMessage data from Gaf to trigger different 3DS flows
   */
  constructThreeDSResponseApiData(
    context: ThreeDSContext,
    eventData: MessageEvent['data'],
  ): ThreedsResponseDataApi {
    const baseResponseData = {
      threeds_state: context.type,
      fx_transaction_id: context.fxTransId,
      request_amount: context.requestAmount,
    };

    switch (context.type) {
      case ThreeDSState.REDIRECT:
        return {
          ...baseResponseData,
          redirect_flow_results: {
            payer_authentication_response: eventData.paresult,
            merchant_data: context.merchantData,
            echo_data: context.echoData,
            cookie_data: context.cookieData,
          },
        };

      case ThreeDSState.IDENTIFY:
        return {
          ...baseResponseData,
          identify_flow_results: {
            server_transaction_id: eventData.threeDSServerTransID,
            completion_indicator: eventData.threeDSCompInd,
            threeds2_token: context.threeDS2token,
          },
        };

      case ThreeDSState.CHALLENGE:
        return {
          ...baseResponseData,
          challenge_flow_results: {
            server_transaction_id: eventData.threeDSServerTransID,
            transaction_status_indicator: eventData.threeDSTransStatus,
            threeds2_token: context.threeDS2token,
          },
        };

      default:
        return assertNever(context);
    }
  }

  /**
   * Process 'returnFrom3DS' events.
   *
   * The backend sends the 'returnFrom3DS' events to the popup window, which
   * then gets posted to the main window. Currently the popup window make sure
   * it's DOM is loaded before dispatching the message to main window. If this
   * process is not followed, a race condition could occur when this routine is
   * modifying popup window's DOM while it is being initialised.
   *
   * @param threeDSPopup The popup window.
   * @param eventData Payload sent with the 'returnFrom3DS' event.
   */
  processPostThreeDS(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    // Retrieve data saved in
    const data = window.localStorage.getItem(`cc_data${eventData.invoiceId}`);
    localStorage.removeItem(`cc_data${eventData.invoiceId}`);
    const back = window.localStorage.getItem(
      `cc_data_back${eventData.invoiceId}`,
    );
    window.localStorage.removeItem(`cc_data_back${eventData.invoiceId}`);

    // Submit post-3ds form in the popup window
    const returnForm = threeDSPopup.document.createElement('form');
    returnForm.method = 'post';
    returnForm.action = back || '';

    this.addFormItem(threeDSPopup, returnForm, 'fielddummy', '');
    this.addFormItem(
      threeDSPopup,
      returnForm,
      'invoice_id',
      eventData.invoiceId,
    );
    this.addFormItem(
      threeDSPopup,
      returnForm,
      'data',
      decodeURIComponent(data || ''),
    );

    switch (eventData.threeDSState) {
      case ThreeDSState.REDIRECT: {
        this.addFormItem(threeDSPopup, returnForm, 'PaRes', eventData.paresult);
        break;
      }
      case ThreeDSState.IDENTIFY: {
        const threeDS2Token = window.localStorage.getItem(
          `cc_data_3ds2token${eventData.invoiceId}`,
        );
        window.localStorage.removeItem(
          `cc_data_3ds2token${eventData.invoiceId}`,
        );

        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDS2Token',
          threeDS2Token || '',
        );
        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDSServerTransID',
          eventData.threeDSServerTransID || '',
        );
        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDSCompInd',
          eventData.threeDSCompInd || '',
        );
        break;
      }
      case ThreeDSState.CHALLENGE: {
        const threeDS2Token = window.localStorage.getItem(
          `cc_data_3ds2token${eventData.invoiceId}`,
        );
        window.localStorage.removeItem(
          `cc_data_3ds2token${eventData.invoiceId}`,
        );

        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDS2Token',
          threeDS2Token || '',
        );
        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDSServerTransID',
          eventData.threeDSServerTransID || '',
        );
        this.addFormItem(
          threeDSPopup,
          returnForm,
          'threeDSTransStatus',
          eventData.threeDSTransStatus || '',
        );
        break;
      }
      default:
        break; // Do nothing
    }

    threeDSPopup.document.body.appendChild(returnForm);
    returnForm.submit();
  }

  /**
   * Get authorization payload
   *
   * @param userId user ID for recurring payment
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId Metrix session ID for tracking purpose
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway Recurring payment gateway
   */
  private getAuthorizationPayload(
    userId: number,
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    creditCardGateway?: string,
    cartId?: number,
    flnBillingData?: string,
    threedsCapability?: ThreedsCapabilityApi,
    threedsResponseData?: ThreedsResponseDataApi,
    invoiceId?: number,
    threedsBrowserInfo?: ThreedsBrowserInfoApi,
  ): ReferenceDepositRequestApi {
    return {
      user_id: userId,
      deposit_method: depositMethod,
      amount,
      currency_id: depositCurrencyId,
      tracking_token: trackingToken,
      recurring_order_id: billingAgreementId,
      threatmetrix_session: threatMetrixSessionId,
      issuing_currency_id: paymentCurrencyId,
      credit_card_gateway: creditCardGateway,
      cart_id: cartId,
      threeds_capability: threedsCapability,
      threeds_response_data: threedsResponseData,
      fln_billing_data: flnBillingData,
      invoice_id: invoiceId,
      threeds_browser_info: threedsBrowserInfo,
    };
  }

  /**
   * Add a form input element.
   *
   * @param windowObj Window object to use call createElement().
   * @param formElement Existing form element to add the input element.
   * @param inputName  Name of the input element.
   * @param inputValue Value of the input element.
   */
  private addFormItem(
    windowObj: Window | undefined,
    formElement: HTMLFormElement,
    inputName: string,
    inputValue: string,
  ): void {
    if (!windowObj) {
      return;
    }
    const dataInput = windowObj.document.createElement('input');
    dataInput.setAttribute('name', inputName);
    dataInput.setAttribute('value', inputValue);
    formElement.appendChild(dataInput);
  }

  /**
   * Construct a html form inside the popup and post it to the target location.
   *
   * @param fromAction The third part request url
   * @param formData The form item for third part post request
   */
  private setupAndPostForm(
    formAction: string,
    formData: readonly FormDataItem[],
  ): void {
    if (this.pwa.isNative()) {
      const formDataObj = JSON.stringify(formData);
      // Open in-app browser with a blank page prior to executing JS script to prevent target page race condition.
      // Whitelist testing - formData will not include JWT item as it is only inserted when the GAF response is in Worldpay flow,
      // see initiate3DSFlow function. Worldpay flow is only triggered for desktop and whitelisted users.
      // Cleanup ticket T270846.
      if (formDataObj.includes('JWT')) {
        const openUrl = 'about:blank';
        this.browser = this.inAppBrowser.open(openUrl);
      }
      const script = `
        const htmlForm = document.createElement('form');
        htmlForm.method = 'post';
        htmlForm.action = '${formAction}';
        const formData = ${JSON.stringify(formData)};

        formData.forEach(elem => {
          const dataInput = document.createElement('input');
          dataInput.setAttribute('name', elem.name);
          dataInput.setAttribute('value', elem.value);
          htmlForm.appendChild(dataInput);
        });

        document.body.appendChild(htmlForm);
        htmlForm.submit();
      `;

      this.browser.executeScript(script);
    } else if (this.threeDSPopUp !== undefined) {
      const htmlForm = this.threeDSPopUp.document.createElement('form');
      htmlForm.method = 'post';
      htmlForm.action = formAction;
      formData.forEach(elem => {
        this.addFormItem(this.threeDSPopUp, htmlForm, elem.name, elem.value);
      });
      this.threeDSPopUp.document.body.appendChild(htmlForm);
      htmlForm.submit();
    }
  }

  /**
   * Direct user to GAF transit endpoint for 3DS.
   * The backend will handle the logic to redirect user to issuer's site
   * depending on the gateway and 3DS version.
   *
   * @param invoiceID number
   */
  private async redirectUserTo3dsWaypoint(invoiceID: number): Promise<void> {
    if (!this.isNative()) {
      if (!this.threeDSPopUp) {
        this.threeDSPopUp = await this.popUpService.openPopUp('threeDSPopup');
      }
      this.threeDSPopUp.location.href = `payments/deposit/threeds-request-redirect.php?invoiceID=${invoiceID}`;
    } else {
      this.browser = this.inAppBrowser.open(
        `payments/deposit/threeds-request-redirect.php?invoiceID=${invoiceID}`,
      );
      this.browser.onExit().then(() => {
        this.paymentsMessaging.pushEvent({
          eventType: PaymentsEventType.CLOSE_OVERLAY,
        });
      });
    }
  }

  /**
   * Takes a string and encodes it as a base64url string.
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   * @param dataStr Input string object.
   */
  private encodeBase64URL(dataStr: string): string {
    const base64 = btoa(dataStr);
    let base64url = base64.split('=')[0]; // Remove any trailing '='s

    base64url = base64url.replace(/\+/g, '-'); // 62nd char of encoding
    base64url = base64url.replace(/\//g, '_'); // 63rd char of encoding

    return base64url;
  }

  /**
   * Generates confirmation url for deposit payload
   *
   * @param backUrl
   * @param enablePopUp
   */
  getConfirmUrl({
    backUrl,
    enablePopUp,
  }: {
    backUrl?: string;
    enablePopUp?: boolean;
  }): string {
    // Force popup if we're in the app
    const isPopup = enablePopUp ?? this.pwa.isNative();
    // TODO: T235225 - migrate confirm.php to webapp
    const url = `${this.location.origin}/payments/deposit/confirm.php`;
    const params = new URLSearchParams();
    if (isPopup) {
      params.append('popup', 'true');
    }
    if (backUrl) {
      params.append('back', backUrl);
    }
    return `${url}?${params.toString()}`;
  }

  /**
   * Make a deposit request to backend for all payment methods that need popup window
   *
   * @param payload Deposit payload for deposit API
   * @param enablePopup If we need show popup window or in-app-browser
   */
  async makeDeposit({
    payload,
    enablePopup,
  }: {
    payload: DepositRequestApi;
    enablePopup: boolean;
  }): Promise<void> {
    const res = await firstValueFrom(
      this.deposit(payload).pipe(untilDestroyed(this)),
    );
    if (res.status === 'error') {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
        errorCode: res.errorCode,
        eventId: res.requestId,
      });
      return;
    }
    const { url } = res.result;
    if (!url) {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
      });
      return;
    }
    if (this.pwa.isNative()) {
      // In native app, open all methods with in-app browser
      this.browser = this.inAppBrowser.open(url);
      let isPending = false;
      this.inAppBrowserMessageSubscription = this.browser
        .onMessage()
        .subscribe(event => {
          const message = this.paymentsUtils.handleInterframeDepositResult(
            event.data,
          );
          if (message) {
            if (
              'paymentsStatus' in message &&
              message.paymentsStatus === PaymentsResultStatus.PENDING &&
              (res.result.deposit_method ===
                DepositMethodApi.PAYPAL_REFERENCE ||
                res.result.deposit_method === DepositMethodApi.PAYPAL)
            ) {
              this.paymentsMessaging.pushOverlay({
                titleText: $localize`Checkout Complete`,
                overlayType: OverlayTypes.PAYPAL_PENDING,
                cartId: payload.cart_id,
              });
              isPending = true;
            }
            this.paymentsMessaging.pushMessage(message);
          } else {
            this.handleError(res.requestId);
          }
          this.browser.close();
        });
      this.browser.onExit().then(() => {
        if (!isPending) {
          this.paymentsMessaging.pushEvent({
            eventType: PaymentsEventType.CLOSE_OVERLAY,
          });
        }
        if (this.inAppBrowserMessageSubscription) {
          this.inAppBrowserMessageSubscription.unsubscribe();
        }
      });
    } else if (enablePopup) {
      const logo = {
        src: this.paymentsUtils.getMethodLogoPath(payload.deposit_method),
        alt: `${payload.deposit_method} logo`,
      };
      let subscription: Subscription | undefined;
      this.paymentsMessaging.pushOverlay({
        brand: logo,
        ctaAction: async () => {
          if (this.paymentPopUp) {
            this.paymentPopUp.focus();
          } else {
            this.paymentPopUp = await this.popUpService.openPopUp(
              `depositPopUp-${payload.deposit_method}`,
            );
            subscription = this.setupRedirectionAndListen(
              this.paymentPopUp,
              res,
              payload.deposit_method,
              payload.cart_id,
            );
          }
        },
        onClose: () => {
          if (!this.paymentPopUp) {
            return;
          }
          this.popUpService.closePopUp();
          this.paymentPopUp = undefined;
          subscription?.unsubscribe();
        },
        overlayType: OverlayTypes.EXTERNAL_POPUP,
        method: payload.deposit_method,
      });
      // move Popup logic to this block T218849
    } else if (url.startsWith('https')) {
      this.location.redirect(url);
    } else {
      this.location.navigateByUrl(url);
    }
  }

  async recurringPayment({
    depositMethod,
    billingAgreementId,
    amount,
    depositCurrencyId,
    paymentCurrencyId,
    threatMetrixSessionId,
    trackingToken,
    cartId,
    creditCard,
    billingData,
    worldpayContext,
  }: {
    depositMethod: DepositMethodApi;
    billingAgreementId: string;
    amount: number;
    depositCurrencyId: number;
    paymentCurrencyId: number;
    threatMetrixSessionId: string;
    trackingToken: string;
    cartId?: number;
    creditCard?: CreditCard;
    billingData?: string;
    worldpayContext?: DepositPageVarsWorldpayDdc;
  }): Promise<void> {
    const depositReference = Date.now();
    this.depositReference = depositReference;

    const isWorldpay = creditCard?.gateway === 'worldpay';
    let cardinalCommerceSessionId;
    // T270846 - Disabling DDC for iOS Capacitor until fixed
    if (isWorldpay && worldpayContext && this.pwa.getPlatform() !== 'ios') {
      if (!creditCard?.bin) {
        this.handleError(undefined, PaymentsErrorType.ERROR_UNDEFINED_DATA);
        return;
      }
      cardinalCommerceSessionId = await this.initWorldpayDdc(
        worldpayContext,
        creditCard?.bin.toString(),
      );
    }

    let response = await this.authorization(
      depositMethod,
      billingAgreementId,
      amount,
      depositCurrencyId,
      paymentCurrencyId,
      threatMetrixSessionId,
      trackingToken,
      cartId,
      creditCard?.gateway,
      billingData,
      ThreedsCapabilityApi.VER1,
      cardinalCommerceSessionId
        ? {
            threeds_state: ThreeDSState.DO_DDC,
            ddc_session_id: cardinalCommerceSessionId,
          }
        : undefined,
      undefined,
      this.generateThreedsBrowserInfo(),
    );
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode, depositMethod);
      return;
    }
    if (this.handleThreeDSResponse(response, depositMethod)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    const threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      undefined,
    );
    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // open popup or in-app-browser
    await this.initThreeDSwindow(threeDSCtx, response, depositReference);

    let messageData;
    if (this.pwa.isNative()) {
      const message = await firstValueFrom(
        this.browser.onMessage().pipe(untilDestroyed(this)),
      );
      messageData = message.data;
    } else {
      messageData = await this.getPostThreeDSProgressPromise(threeDSCtx.type);
    }

    // Signifies that we are in Worldpay flow. We need to send the stored
    // machine cookie back in the second authorize request after 3DS challenge is completed.
    if (
      messageData.threeDSServerTransID === '' &&
      messageData.threeDSTransStatus === ''
    ) {
      const threeDSChallengeCookie = await firstValueFrom(
        this.localStorage
          .get('threeDSChallengeCookie')
          .pipe(untilDestroyed(this)),
      );
      this.localStorage.set('threeDSChallengeCookie', '');
      messageData.threeDSServerTransID = threeDSChallengeCookie;
    }

    const threeDsData = this.constructThreeDSResponseApiData(
      threeDSCtx,
      messageData,
    );
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    response = await this.authorization(
      depositMethod,
      billingAgreementId,
      amount,
      depositCurrencyId,
      paymentCurrencyId,
      threatMetrixSessionId,
      trackingToken,
      cartId,
      creditCard?.gateway,
      billingData,
      ThreedsCapabilityApi.VER1,
      threeDsData,
      threeDSCtx.invoiceId,
    );
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }
    this.handleThreeDSResponse(response, depositMethod);
  }

  /**
   * Handle all credit card deposit request including 3DS popup opening.
   *
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async creditCardDeposit(
    confirmation: DepositRequestConfirmationApi,
    worldpayContext: DepositPageVarsWorldpayDdc | undefined,
  ): Promise<void> {
    // First request
    const depositReference = Date.now();
    this.depositReference = depositReference;
    const depositConfirmation = confirmation;
    let response = await this.depositUpdate(depositConfirmation);

    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }

    // Perform DDC for Worldpay
    if (
      response.result.threeds_data &&
      response.result.threeds_data.threeds_state === ThreeDSState.DO_DDC &&
      worldpayContext
    ) {
      let flnBillingDataObj;
      if (depositConfirmation.fln_billing_data) {
        flnBillingDataObj = JSON.parse(depositConfirmation.fln_billing_data);
      }
      if (!flnBillingDataObj) {
        this.handleError(
          response.requestId,
          PaymentsErrorType.ERROR_UNDEFINED_DATA,
        );
        return;
      }
      const bin = flnBillingDataObj.value.find(
        (arr: { name: string; value: any }) => arr.name === 'iin',
      );
      if (!bin) {
        this.handleError(
          response.requestId,
          PaymentsErrorType.ERROR_UNDEFINED_DATA,
        );
        return;
      }
      const cardinalCommerceSessionId = await this.initWorldpayDdc(
        worldpayContext,
        bin?.value,
      );
      const ddcSessionId = {
        name: 'ddcSessionId',
        value: cardinalCommerceSessionId,
      };
      flnBillingDataObj.value.push(ddcSessionId);
      const flnBlnData = JSON.stringify(flnBillingDataObj);
      const confirmationAfterDdc: DepositRequestConfirmationApi = {
        user_id: depositConfirmation.user_id,
        invoice_id: response.result.threeds_data.invoice_id,
        deposit_method: depositConfirmation.deposit_method,
        fln_billing_data: flnBlnData,
        credit_card_gateway: response.result?.threeds_data?.credit_card_gateway,
        cart_id: depositConfirmation.cart_id,
        threeds_capability: depositConfirmation.threeds_capability,
        tracking_token: depositConfirmation.tracking_token,
        action: depositConfirmation.action,
        may_require_mandate: depositConfirmation.may_require_mandate,
      };
      response = await this.depositUpdate(confirmationAfterDdc);
      if (response.status !== 'success') {
        this.handleError(response.requestId, response.errorCode);
        return;
      }
    }
    if (this.handleThreeDSResponse(response, confirmation.deposit_method)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    const threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      undefined,
    );

    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // open popup or in-app-browser
    await this.initThreeDSwindow(
      threeDSCtx,
      response,
      depositReference,
      confirmation,
    );
  }

  /**
   * Continue credit card 3DS deposit flow when popup overlay CTA is clicked.
   */
  async continueThreeDSCreditCardDeposit(
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    threeDSCtx: ThreeDSContext | undefined,
    depositReference: number,
    depositConfirmation?: DepositRequestConfirmationApi,
  ): Promise<void> {
    let depositUpdateResponse = response;
    let continuethreeDSCtx = threeDSCtx;
    if (!continuethreeDSCtx) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    if (!depositConfirmation) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    let depositConfirmationRequest = await this.generateConfirmationRequest(
      continuethreeDSCtx,
      depositConfirmation,
    );

    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositUpdateResponse = await this.depositUpdate(
      depositConfirmationRequest,
    );

    // 3DS is in progress
    // Received an error response from API
    if (depositUpdateResponse.status !== 'success') {
      this.handleError(
        depositUpdateResponse.requestId,
        depositUpdateResponse.errorCode,
      );
      return;
    }

    if (
      this.handleThreeDSResponse(
        depositUpdateResponse,
        depositConfirmation?.deposit_method,
        true,
      )
    ) {
      return;
    }

    // 3DS is triggered
    if (!depositUpdateResponse.result.threeds_data) {
      return this.handleError(depositUpdateResponse.requestId);
    }

    continuethreeDSCtx = this.createThreeDSContext(
      depositUpdateResponse.result.threeds_data,
      continuethreeDSCtx,
    );
    if (!continuethreeDSCtx) {
      return this.handleError(depositUpdateResponse.requestId);
    }
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositConfirmationRequest = await this.generateConfirmationRequest(
      continuethreeDSCtx,
      depositConfirmationRequest,
    );
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositUpdateResponse = await this.depositUpdate(
      depositConfirmationRequest,
    );
    if (depositUpdateResponse.status !== 'success') {
      this.handleError(
        depositUpdateResponse.requestId,
        depositUpdateResponse.errorCode,
      );
      return;
    }
    this.handleThreeDSResponse(
      depositUpdateResponse,
      depositConfirmation?.deposit_method,
      true,
    );
  }

  /**
   * initialise the popup window for webapp or in app browser for Capacitor App.
   */
  async initThreeDSwindow(
    threeDSCtx: ThreeDSContext,
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    depositReference: number,
    depositConfirmation?: DepositRequestConfirmationApi,
  ): Promise<void> {
    this.tracking.trackCustomEvent('ThreeDSRequired');
    if (this.pwa.isNative()) {
      // First time 3DS triggered
      if (depositConfirmation) {
        this.continueThreeDSCreditCardDeposit(
          response,
          threeDSCtx,
          depositReference,
          depositConfirmation,
        );
      } else {
        // Recurring 3DS triggered
        this.initiate3DSFlow({ context: threeDSCtx, useBackend: true });
      }
    }
    this.paymentsMessaging.pushOverlay({
      brand: {
        src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WIRE),
        alt: 'Bank Logo',
      },
      ctaAction: async () => {
        // If popup has been opened before don't initiate
        if (this.threeDSPopUp) {
          const threeDSPopUp = await this.popUpService.openPopUp(
            'threeDSPopup',
          );
          threeDSPopUp.focus();
          return;
        }

        const threeDSPopUp = await this.popUpService.openPopUp('threeDSPopup');
        if (threeDSPopUp) {
          this.threeDSPopUp = threeDSPopUp;
          // First time 3DS triggered
          if (depositConfirmation) {
            await this.continueThreeDSCreditCardDeposit(
              response,
              threeDSCtx,
              depositReference,
              depositConfirmation,
            );
          } else {
            // Recurring 3DS triggered
            this.initiate3DSFlow({ context: threeDSCtx, useBackend: true });
          }
        } else {
          return this.paymentsMessaging.pushError({
            errorType: PaymentsErrorType.ERROR,
            errorMessage: 'Failed to open popup window',
          });
        }
      },
      onClose: () => this.cancelThreeDSFlow(),
      overlayType: OverlayTypes.CC_3DS,
    });
  }

  /**
   * generate new confirmation request payload based on the post message
   *
   * @param threeDSCtx 3DS context data object
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async generateConfirmationRequest(
    threeDSCtx: ThreeDSContext,
    confirmation: DepositRequestConfirmationApi,
  ): Promise<DepositRequestConfirmationApi> {
    this.initiate3DSFlow({ context: threeDSCtx, useBackend: true });
    let messageData;
    if (this.pwa.isNative()) {
      const message = await firstValueFrom(
        this.browser.onMessage().pipe(untilDestroyed(this)),
      );
      messageData = message.data;
    } else {
      messageData = await this.getPostThreeDSProgressPromise(threeDSCtx.type);
    }
    if (
      messageData.threeDSServerTransID === '' &&
      messageData.threeDSTransStatus === ''
    ) {
      const threeDSChallengeCookie = await firstValueFrom(
        this.localStorage
          .get('threeDSChallengeCookie')
          .pipe(untilDestroyed(this)),
      );
      // remove threeDSChallengeCookie cache by making it to empty
      this.localStorage.set('threeDSChallengeCookie', '');
      messageData.threeDSServerTransID = threeDSChallengeCookie;
    }
    if (messageData.threeDSFailure) {
      this.closeOverlay();
      // No need to emit the error as this.paymentsMessaging service seems
      // to magically receive it.
      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
        this.threeDSPopUp = undefined;
      }
    }
    return {
      user_id: confirmation.user_id,
      deposit_method: DepositMethodApi.FLN_BILLING,
      fln_billing_data: confirmation.fln_billing_data,
      credit_card_gateway: threeDSCtx.ccGateway,
      cart_id: confirmation.cart_id,
      threeds_capability: ThreedsCapabilityApi.VER2,
      invoice_id: threeDSCtx.invoiceId,
      tracking_token: confirmation.tracking_token,
      threeds_response_data: this.constructThreeDSResponseApiData(
        threeDSCtx,
        messageData,
      ),
      action: confirmation.action,
      may_require_mandate: confirmation.may_require_mandate,
    };
  }

  /**
   * Handle the response from credit card deposit API call.
   *
   * @param response Response from deposit API
   */
  private handleThreeDSResponse(
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
    method?: DepositMethodApi,
    isFrom3dsWindow = false,
  ): boolean {
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return true;
    }
    if (!response.result.is_threeds_triggered) {
      if (this.browser && this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.disableOverlayCloseEvent();
        this.popUpService.closePopUp();
        this.threeDSPopUp = undefined;
      }
      // Deposit is successful.
      this.paymentsMessaging.pushResult({
        method,
        paymentsStatus: PaymentsResultStatus.SUCCESS,
        is3ds: isFrom3dsWindow,
      });
      return true;
    }
    // 3DS is triggered
    if (!response.result.threeds_data) {
      this.handleError(response.requestId);
      return true;
    }
    return false;
  }

  /**
   * Close overlay
   */
  private closeOverlay(): void {
    this.paymentsMessaging.pushEvent({
      eventType: PaymentsEventType.CLOSE_OVERLAY,
    });
  }

  /**
   * Handle the error response from API call
   *
   * @param eventId Event ID from API response
   * @param errorCode The error code from the API response
   * @param paymentMethod Payment method from API response
   */
  private handleError(
    eventId?: string,
    errorCode?: string,
    paymentMethod?: string,
  ): void {
    this.closeOverlay();
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
      this.threeDSPopUp = undefined;
    }
    this.paymentsMessaging.pushError({
      errorCode,
      errorType: errorCode
        ? PaymentsErrorType.ERROR
        : PaymentsErrorType.ERROR_UNKNOWN,
      eventId,
      paymentMethod,
    });
  }

  /**
   * Get result post message from GAF
   */
  getThreeDSDepositResultPromise(): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getFilteredMessageStream(
          event => !!event.data.threeDSFailure || !!event.data.threeDSSuccess,
        )
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * Get return post message from GAF
   */
  getPostThreeDSPromise(): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getFilteredMessageStream(
          event =>
            !!event.data.threeDSFailure ||
            (!!event.data.type &&
              event.data.type === ThreeDSMessageType.RETURN),
        )
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * Get 3DS post message based on 3DS state.
   *
   * @param threeDSState 3DS state for different flows
   */
  private getPostThreeDSProgressPromise(
    threeDSState: string,
  ): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getFilteredMessageStream(
          event =>
            event.data.threeDSState === threeDSState &&
            (!!event.data.threeDSFailure ||
              (!!event.data.type &&
                event.data.type === ThreeDSMessageType.RETURN)),
        )
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * Emit error message to show the banner in deposit page
   *
   * @param errorMessage Error message from API response
   * @param errorType Error type from API response
   */
  emitErrorMessage(
    errorMessage: string,
    errorType:
      | PaymentsErrorType.ERROR
      | PaymentsErrorType.ERROR_AUTH
      | PaymentsErrorType.ERROR_INIT
      | PaymentsErrorType.ERROR_UNKNOWN,
  ): void {
    this.paymentsMessaging.pushError({
      errorMessage,
      errorType,
    });
  }

  /**
   * Cancel the 3DS flow
   */
  cancelThreeDSFlow(): void {
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
      this.threeDSPopUp = undefined;
    }
    this.paymentsMessaging.pushResult({
      paymentsStatus: PaymentsResultStatus.INTERRUPTED,
    });
  }

  /**
   *  Continue 3DS flow if challenged further, or finalise the flow.
   *
   * @param threeDSPopup Window object for 3DS popup
   * @param eventData PostMessage data from Gaf
   */
  finaliseOrContinueThreeDSFlow(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    if (!!eventData.type && eventData.type === ThreeDSMessageType.TRIGGER) {
      this.getPostThreeDSPromise().then(data =>
        this.processThreeDSResults(threeDSPopup, data),
      );
    } else {
      if (!!eventData.type && eventData.type === 'success') {
        // Disable overlay being closed immediately after the popup window
        // is closed. The overlay displays additional info and has logic to
        // redirect the user to dashboard.
        this.popUpService.disableOverlayCloseEvent();
        this.paymentsMessaging.pushResult({
          paymentsStatus: PaymentsResultStatus.SUCCESS,
        });
      } else {
        this.closeOverlay();
        this.emitErrorMessage('3DS deposit failed', PaymentsErrorType.ERROR);
      }

      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
        this.threeDSPopUp = undefined;
      }
    }
  }

  /**
   *  Handling a result message from 3DS flow
   *
   * @param threeDSPopup Window object for 3DS popup
   * @param eventData PostMessage data from Gaf
   */
  processThreeDSResults(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    if (eventData.threeDSFailure) {
      this.closeOverlay();
      // No need to emit the error as this.paymentsMessaging service seems
      // to magically receive it.
      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
        this.threeDSPopUp = undefined;
      }
    } else {
      this.getThreeDSFinaliseOrContinuePromise().then(resultData =>
        this.finaliseOrContinueThreeDSFlow(threeDSPopup, resultData),
      );
      this.processPostThreeDS(threeDSPopup, eventData);
    }
  }

  /**
   *  TODO: T267853 - [PSD2]: Is there a better way to restructure these promises?
   *  In the new 3DS flows, a subsequent trigger message (for challenge)
   *  could be sent after the initial trigger message for redirect.
   */
  getThreeDSFinaliseOrContinuePromise(): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getFilteredMessageStream(
          event =>
            !!event.data.threeDSFailure ||
            !!event.data.threeDSSuccess ||
            (!!event.data.type &&
              event.data.type === ThreeDSMessageType.TRIGGER),
        )
        .pipe(untilDestroyed(this)),
    );
  }

  /**
   * setup popup window for third part payment methods.
   * @param popup Window object for 3DS popup
   * @param response Deposit API response
   * @param method
   * @param cartId
   */
  private setupRedirectionAndListen(
    popup: Window,
    response: SuccessResponseData<DepositRequestActionApi>,
    method: DepositMethodApi,
    cartId?: number,
  ): Subscription | undefined {
    const { url } = response.result;
    if (!url) {
      this.paymentsMessaging.pushError({ errorType: PaymentsErrorType.ERROR });
      return undefined;
    }

    // redirect and listen for deposit result
    popup.location.href = url;
    const depositSubscription = this.paymentsUtils
      .listenForDepositResult()
      .subscribe(data => {
        depositSubscription.unsubscribe();
        this.popUpService.closePopUp();
        this.paymentPopUp = undefined;
        const message = this.paymentsUtils.handleInterframeDepositResult(data);
        if (message) {
          if (
            'paymentsStatus' in message &&
            message.paymentsStatus === PaymentsResultStatus.PENDING &&
            (response.result.deposit_method ===
              DepositMethodApi.PAYPAL_REFERENCE ||
              response.result.deposit_method === DepositMethodApi.PAYPAL)
          ) {
            this.popUpService.disableOverlayCloseEvent();
            this.paymentsMessaging.pushOverlay({
              titleText: $localize`Checkout Complete`,
              overlayType: OverlayTypes.PAYPAL_PENDING,
              cartId,
            });
          }
          const messageWithMethod = {
            ...message,
            method,
          };
          this.paymentsMessaging.pushMessage(messageWithMethod);
        } else {
          this.handleError(response.requestId);
        }
      });
    return depositSubscription;
  }

  processUpgradesAndMilestones(
    requestData: PreprocessMilestonesRequest,
  ): Promise<PreprocessMilestonesResponse | undefined> {
    return firstValueFrom(
      this.freelancerHttp
        .post<PreprocessMilestonesResponse>(
          `payment/on-preprocess-upgrades-and-milestones.php`,
          requestData,
          {
            isGaf: true,
            serializeBody: true,
          },
        )
        .pipe(
          map(response =>
            response.status === 'success' ? response.result : undefined,
          ),
          untilDestroyed(this),
        ),
    );
  }

  /**
   * Temporary function to check if use Capacitor App.
   */
  isNative(): boolean {
    return this.pwa.isNative();
  }

  checkIfCaptchaRequired(url: string): Promise<boolean> {
    return firstValueFrom(
      this.http.get(url, { responseType: 'text' }).pipe(untilDestroyed(this)),
    )
      .then(_ => false)
      .catch(
        e =>
          // Means it is a CORS error, which shall be caused by the captcha
          e.status === 0,
      );
  }

  getWorldPayDdcResponseStream(url: string): Observable<MessageEvent['data']> {
    return fromEvent<MessageEvent['data']>(window, 'message').pipe(
      filter(m => m.origin === url),
      map(m => m.data),
    );
  }

  getWorldPayDdcPromise(url: string): Promise<MessageEvent['data']> {
    return firstValueFrom(
      this.paymentsUtils
        .getExternalServiceMessageStream(url, event => event.data !== undefined)
        .pipe(untilDestroyed(this)),
    );
  }

  getWorldPayFormField(targetId: string): HTMLElement | undefined {
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      return targetElement;
    }
  }

  /**
   * Initiate worldpay device data collection process
   */
  async initWorldpayDdc(
    worldpayContext: DepositPageVarsWorldpayDdc,
    bin: string,
  ): Promise<string> {
    const cardinalCommercePostMessage = this.getWorldPayDdcPromise(
      worldpayContext.originUrl,
    );

    const formBin = this.getWorldPayFormField('formBin');
    formBin?.setAttribute('value', bin);
    const formJwt = this.getWorldPayFormField('formJwt');
    formJwt?.setAttribute('value', worldpayContext.jwt);
    const ddcForm = this.getWorldPayFormField('ddcForm') as HTMLFormElement;
    ddcForm?.setAttribute('action', worldpayContext.url);
    ddcForm.submit();

    const cardinalCommercePostMessageObject = JSON.parse(
      await cardinalCommercePostMessage,
    );
    let cardinalCommerceSessionId = '';
    if (cardinalCommercePostMessageObject.Status) {
      cardinalCommerceSessionId = cardinalCommercePostMessageObject.SessionId;
    }
    return cardinalCommerceSessionId;
  }

  getDepositPageContext(): DatastoreCollection<DepositPageVarsCollection> {
    const queryParam = (param: keyof Params): Observable<string> =>
      this.activatedRoute.queryParams.pipe(map(v => v[param]));
    return this.datastore.collection<DepositPageVarsCollection>(
      'depositPageVars',
      query =>
        query
          .where('amount', '==', queryParam('amount').pipe(map(toNumber)))
          .where('back', '==', queryParam('back'))
          .where('orgBackUrl', '==', queryParam('backUrl'))
          .where('bidderId', '==', queryParam('bidderId'))
          .where('contractQuantity', '==', queryParam('contract_quantity'))
          .where('currency', '==', queryParam('currency').pipe(map(toNumber)))
          .where('dataToken', '==', queryParam('dataToken'))
          .where('descr', '==', queryParam('descr'))
          .where('durationCycle', '==', queryParam('duration_cycle'))
          .where('durationType', '==', queryParam('duration_type'))
          .where('entryId', '==', queryParam('entry_id'))
          .where(
            'hiremeInitialMilestoneTracking',
            '==',
            queryParam('hireme_initial_milestone_tracking'),
          )
          .where('pid', '==', queryParam('id')) // transform id to pid
          .where('listingId', '==', queryParam('listing_id'))
          .where('membershipCoupon', '==', queryParam('membership_coupon'))
          .where('membershipRef', '==', queryParam('membership_ref'))
          .where('milestoneAmount', '==', queryParam('milestone_amount'))
          .where('milestoneRequestId', '==', queryParam('milestone_request_id'))
          .where('nextAction', '==', queryParam('nextAction'))
          .where('oneTimeNotify', '==', queryParam('one_time_notify'))
          .where('packageId', '==', queryParam('package_id'))
          .where('priorityRef', '==', queryParam('priority_ref'))
          .where('redirected', '==', queryParam('redirected'))
          .where('refAction', '==', queryParam('refAction'))
          .where('returnToProject', '==', queryParam('return_to_project'))
          .where(
            'toCorporateOnboarding',
            '==',
            queryParam('to_corporate_onboarding'),
          )
          .where(
            'toFinancialDashboard',
            '==',
            queryParam('to_financial_dashboard'),
          )
          .where('orgTrackingToken', '==', queryParam('trackingToken'))
          .where('uid', '==', queryParam('uid'))
          .where('upgradeOptions', '==', queryParam('upgrade_options'))
          .where('username', '==', queryParam('username'))
          .where('verifiedPayment', '==', queryParam('verified_payment'))
          .where('paymentItem', '==', queryParam('paymentItem'))
          .where('cartId', '==', queryParam('cartId').pipe(map(toNumber))),
    );
  }
}
